import { type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronLeft, ChevronRight } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";

// Glass capsule with a prev arrow, "N left", and a next arrow. Shared by the compose
// TopNav (TitleBar) and the summary TopNav so the cycler look stays identical.
const Cycler = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  height: theme.fieldSalesTwo.spacing[48],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const CyclerArrow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface BatchCyclerProps {
  count: number;
  onPrev: () => void;
  onNext: () => void;
}

export function BatchCycler({ count, onPrev, onNext }: BatchCyclerProps): ReactNode {
  const t = useTranslations("fieldSalesTwo.recaps");

  return (
    <Cycler>
      <CyclerArrow type="button" aria-label={t("a11y.previous")} onClick={onPrev}>
        <ChevronLeft width={18} height={18} />
      </CyclerArrow>
      <span>{t("compose.left", { count })}</span>
      <CyclerArrow type="button" aria-label={t("a11y.next")} onClick={onNext}>
        <ChevronRight width={18} height={18} />
      </CyclerArrow>
    </Cycler>
  );
}
